// src/components/common/AnimatedCanvas.tsx
import { useRef, useEffect } from 'react';

const AnimatedCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;

      constructor() {
        this.x = Math.random() * (canvas?.width ?? 0); // Garantia adicional
        this.y = Math.random() * (canvas?.height ?? 0);
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 1.5;
        this.size = Math.random() * 3 + 2;
        this.color = '#0047FF';
      }

      draw(context: CanvasRenderingContext2D) {
        context.beginPath();
        context.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        context.fillStyle = this.color;
        context.fill();
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > (canvas?.width ?? 0)) this.vx *= -1;
        if (this.y < 0 || this.y > (canvas?.height ?? 0)) this.vy *= -1;
      }
    }

    const particles: Particle[] = Array.from({ length: 150 }, () => new Particle());

    const connectParticles = (context: CanvasRenderingContext2D) => {
      particles.forEach((particleA, i) => {
        for (let j = i + 1; j < particles.length; j++) {
          const particleB = particles[j];
          const dx = particleA.x - particleB.x;
          const dy = particleA.y - particleB.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            context.beginPath();
            context.moveTo(particleA.x, particleA.y);
            context.lineTo(particleB.x, particleB.y);
            context.strokeStyle = '#0047FF';
            context.lineWidth = 1;
            context.stroke();
          }
        }
      });
    };

    // --- Interação de mouse (aditivo): ponto extra que segue o cursor ---
    // Não altera as partículas/linhas existentes; apenas desenha por cima.
    const mouse = { x: 0, y: 0 };
    const follower = { x: 0, y: 0 };
    let mouseActive = false;

    const handlePointerMove = (event: MouseEvent) => {
      const overForm = !!(event.target as Element | null)?.closest?.('.auth-container');
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      if (overForm) {
        // Sobre o formulário: o ponto extra some e o mouse atua só no form.
        mouseActive = false;
        return;
      }
      if (!mouseActive) {
        // (Re)entrada no fundo: alinha o seguidor ao cursor para não riscar
        // uma linha longa vinda da última posição.
        follower.x = mouse.x;
        follower.y = mouse.y;
      }
      mouseActive = true;
    };

    const handlePointerOut = (event: MouseEvent) => {
      // Cursor deixou a janela → o ponto extra some.
      if (!event.relatedTarget) mouseActive = false;
    };

    let rafId = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((particle) => {
        particle.update();
        particle.draw(ctx);
      });
      connectParticles(ctx);

      // Ponto extra que segue o mouse e se conecta às partículas próximas.
      if (mouseActive) {
        // Glide suave em direção ao cursor.
        follower.x += (mouse.x - follower.x) * 0.15;
        follower.y += (mouse.y - follower.y) * 0.15;

        particles.forEach((particle) => {
          const dx = particle.x - follower.x;
          const dy = particle.y - follower.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 160) {
            ctx.beginPath();
            ctx.moveTo(follower.x, follower.y);
            ctx.lineTo(particle.x, particle.y);
            ctx.strokeStyle = '#0047FF';
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });

        ctx.beginPath();
        ctx.arc(follower.x, follower.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = '#0047FF';
        ctx.fill();
      }

      rafId = requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseout', handlePointerOut);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseout', handlePointerOut);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} className="background-canvas"></canvas>;
};

export default AnimatedCanvas;
